import type { DocumentRepository } from "../../domain/repositories/document-repository";
import type {
  DocumentListModel,
  DocumentModel,
  DocumentUesModel,
  UploadFilesResponseModel,
} from "../models/document-model";
import {
  toDocumentDetail,
  toDocumentList,
  toDocumentUes,
  toUploadResult,
} from "../mappers/document-mapper";
import {
  parseResponse,
  parseSuccessResponse,
} from "@/core/data/parse-response";
import { fetchWithAuth } from "@/core/data/fetch-with-auth";

export const httpDocumentRepository: DocumentRepository = {
  list: async ({ kbId, page, limit }) => {
    const params = new URLSearchParams({
      kb_id: kbId,
      page: String(page),
      limit: String(limit),
    });
    const res = await fetchWithAuth(`/api/v1/documents?${params}`);
    const data = await parseResponse<DocumentListModel>(res);
    return toDocumentList(data);
  },

  getById: async (id) => {
    const res = await fetchWithAuth(`/api/v1/documents/${id}`);
    const data = await parseResponse<DocumentModel>(res);
    return toDocumentDetail(data);
  },

  upload: async ({ kbId, files }) => {
    const form = new FormData();
    form.append("kb_id", kbId);
    for (const file of files) {
      form.append("files", file);
      form.append("mtimes", String(file.lastModified));
    }
    const res = await fetchWithAuth("/api/v1/documents/upload", {
      method: "POST",
      body: form,
    });
    const data = await parseResponse<UploadFilesResponseModel>(res);
    return toUploadResult(data);
  },

  download: async (docId) => {
    const res = await fetchWithAuth(`/api/v1/documents/${docId}/download`);
    if (!res.ok) {
      const body: { message?: string } = await res.json().catch(() => ({}));
      throw new Error(
        body.message ?? `Request failed with status ${res.status}`,
      );
    }
    return res.blob();
  },

  parse: async (docIds) => {
    const res = await fetchWithAuth("/api/v1/documents/parse", {
      method: "POST",
      body: JSON.stringify({ doc_ids: docIds }),
    });
    await parseSuccessResponse(res);
  },

  reparse: async (docIds) => {
    const res = await fetchWithAuth("/api/v1/documents/reparse", {
      method: "POST",
      body: JSON.stringify({ doc_ids: docIds }),
    });
    await parseSuccessResponse(res);
  },

  cancel: async (id) => {
    const res = await fetchWithAuth(`/api/v1/documents/${id}/cancel`, {
      method: "POST",
    });
    await parseSuccessResponse(res);
  },

  cancelAll: async (kbId) => {
    const res = await fetchWithAuth(
      `/api/v1/knowledge-bases/${kbId}/documents:cancel-all`,
      { method: "POST" },
    );
    const data = await parseResponse<{ cancelled_count: number }>(res);
    return data.cancelled_count;
  },

  retry: async (id) => {
    const res = await fetchWithAuth(`/api/v1/documents/${id}/retry`, {
      method: "POST",
    });
    await parseSuccessResponse(res);
  },

  redoMetadata: async (id) => {
    const res = await fetchWithAuth(`/api/v1/documents/${id}/metadata`, {
      method: "POST",
    });
    await parseSuccessResponse(res);
  },

  getUes: async (docId) => {
    const res = await fetchWithAuth(`/api/v1/documents/${docId}/ues`);
    const data = await parseResponse<DocumentUesModel>(res);
    return toDocumentUes(data);
  },

  reextractUes: async (docId, docTypeId) => {
    const res = await fetchWithAuth(
      `/api/v1/documents/${docId}/ues:reextract`,
      {
        method: "POST",
        body: JSON.stringify(
          docTypeId == null ? {} : { doc_type_id: docTypeId },
        ),
      },
    );
    const data = await parseResponse<{ doc_id: string; job_id: string }>(res);
    return { docId: data.doc_id, jobId: data.job_id };
  },

  delete: async (id) => {
    const res = await fetchWithAuth(`/api/v1/documents/${id}`, {
      method: "DELETE",
    });
    await parseSuccessResponse(res);
  },
};
